import {
  Alert02Icon,
  BalanceScaleIcon,
  CreditCardIcon,
  File02Icon,
  Home09Icon,
  Shield01Icon,
  UserCircle02Icon,
  UserMultiple02Icon,
  Wallet01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import type { IconSvgElement } from "@hugeicons/react";
import type {
  ReportApplicationFacts,
  ReportSection
} from "api/owner-screening-report";

/**
 * How each figure is rendered. Kept here rather than on the server because it is
 * presentation: the payload carries numbers so the wording can change without a
 * deploy of the API.
 */
export type FieldFormat =
  | "money"
  | "number"
  | "ratio"
  | "percent"
  | "months"
  | "convictions"
  | "records"
  | "matches"
  | "date"
  | "recommendation";

/**
 * Labels for the group totals, keyed by the metric names the application page's
 * own metric builder uses — the report is built by that same builder, so these
 * are the same cards a partner sees, under the same names.
 */
export const METRIC_LABELS: Record<string, string> = {
  verified_gross_income: "Gross income",
  verified_net_income: "Net income",
  verified_gross_income_to_rent: "Gross income to rent",
  verified_net_income_to_rent: "Net income to rent",
  debt_to_verified_gross_income: "Debt to gross income",
  average_credit_score: "Average credit score",
  lowest_credit_score: "Lowest credit score",
  highest_credit_score: "Highest credit score",
  people_with_no_credit_score: "People with no credit score"
};

/** Metric values arrive as plain numbers; this says what each one means. */
export const METRIC_FORMATS: Record<string, FieldFormat> = {
  verified_gross_income: "money",
  verified_net_income: "money",
  verified_gross_income_to_rent: "ratio",
  verified_net_income_to_rent: "ratio",
  debt_to_verified_gross_income: "percent",
  average_credit_score: "number",
  lowest_credit_score: "number",
  highest_credit_score: "number",
  people_with_no_credit_score: "number"
};

/** The application's own facts, in the order the summary lists them. */
export const APPLICATION_FACT_ROWS: {
  key: keyof ReportApplicationFacts;
  label: string;
  format: FieldFormat;
}[] = [
  { key: "application_rent", label: "Application rent", format: "money" },
  { key: "desired_move_in", label: "Desired move-in", format: "date" },
  { key: "desired_lease_term", label: "Desired lease term", format: "months" },
  { key: "recommendation", label: "Recommendation", format: "recommendation" }
];

export const RECOMMENDATION_LABELS: Record<string, string> = {
  approve: "Approve",
  conditional_approve: "Approve with conditions",
  decline: "Decline"
};

/**
 * Illustrative only. The setup page previews a sample application, so there is
 * no real share to quote — this stands in for whatever the sender will write,
 * and is shown only while the partner allows messages at all.
 */
export const SAMPLE_SHARE_MESSAGE =
  "Hi, here are the applicants for Unit 4B. Let me know if you're comfortable moving forward.";

export const IDENTITY_STATUS_LABELS: Record<string, string> = {
  passed: "Passed",
  passed_with_warnings: "Passed with warnings",
  failed: "Failed"
};

export const RENTAL_VERIFICATION_STATUS_LABELS: Record<string, string> = {
  completed: "Completed",
  partially_completed: "Partially completed",
  pending: "Pending"
};

/**
 * The line under the identity chip. `passed` is the design's wording; the other
 * two are DERIVED, NOT SPECIFIED — the walkthrough only shows a passing card.
 */
export const IDENTITY_STATUS_SUBTEXT: Record<string, string> = {
  passed: "Records, document and selfie checks passed",
  passed_with_warnings:
    "Records, document and selfie checks passed with warnings",
  failed: "Records, document or selfie checks did not pass"
};

type SectionFieldKey = keyof Omit<ReportSection, "section">;

/**
 * The per-person sections, in the order the report lists them — the same order
 * and the same icons as the partner's setup page, so what a partner switched on
 * there is recognisable here.
 *
 * Identity verification, rental verification and warning signals carry no field
 * list: each reads as one sentence rather than a column of figures, and the card
 * renders them itself.
 */
const SECTIONS: {
  key: string;
  label: string;
  icon: IconSvgElement;
  fields: { key: SectionFieldKey; label: string; format: FieldFormat }[];
}[] = [
  {
    key: "identity_verification",
    label: "Identity verification",
    icon: UserCircle02Icon,
    fields: []
  },
  {
    key: "income",
    label: "Income",
    icon: Wallet01Icon,
    fields: [
      {
        key: "verified_gross_monthly_income",
        label: "Verified gross monthly income",
        format: "money"
      },
      {
        key: "verified_net_monthly_income",
        label: "Verified net monthly income",
        format: "money"
      },
      {
        key: "gross_income_to_rent",
        label: "Gross income to rent",
        format: "ratio"
      }
    ]
  },
  {
    key: "credit",
    label: "Credit",
    icon: CreditCardIcon,
    fields: [
      { key: "credit_score", label: "Credit score", format: "number" },
      {
        key: "past_due_accounts",
        label: "Past due accounts",
        format: "number"
      },
      {
        key: "balance_of_collections",
        label: "Balance of collections",
        format: "money"
      },
      { key: "bankruptcies", label: "Bankruptcy", format: "number" }
    ]
  },
  {
    key: "criminal",
    label: "Criminal",
    icon: Shield01Icon,
    fields: [
      { key: "felony_convictions", label: "Felony", format: "convictions" },
      {
        key: "misdemeanor_convictions",
        label: "Misdemeanor",
        format: "convictions"
      },
      {
        key: "national_sex_offender",
        label: "National sex offender",
        format: "records"
      },
      { key: "ofac", label: "OFAC", format: "matches" }
    ]
  },
  {
    key: "housing_court",
    label: "Housing court",
    icon: BalanceScaleIcon,
    fields: [
      { key: "events_reported", label: "Events reported", format: "number" }
    ]
  },
  {
    key: "rental_verification",
    label: "Rental verification",
    icon: File02Icon,
    fields: []
  },
  {
    key: "household",
    label: "Household",
    icon: UserMultiple02Icon,
    fields: [
      { key: "pets", label: "Pets", format: "number" },
      { key: "vehicles", label: "Vehicles", format: "number" }
    ]
  },
  {
    key: "housing_history",
    label: "Housing history",
    icon: Home09Icon,
    fields: [
      { key: "residences", label: "Residences", format: "number" },
      {
        key: "total_months_of_residence",
        label: "Total time in residence",
        format: "months"
      },
      {
        key: "average_monthly_payment",
        label: "Average monthly payment",
        format: "money"
      }
    ]
  },
  {
    key: "warning_signals",
    label: "Warning signals",
    icon: Alert02Icon,
    fields: []
  }
];

export const SECTIONS_BY_KEY = Object.fromEntries(
  SECTIONS.map((section) => [section.key, section])
);
